import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { Plus, MessageSquare, Trash2, Pencil, Eye, EyeOff, Send } from "lucide-react";

export default function MentorReports() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [reports, setReports] = useState([]);
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ team_id: "", title: "", body: "", shared_with_team: false });
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin" || user?.role === "faculty";
  const isMentor = user?.role === "mentor";

  const load = async () => {
    const [r, t] = await Promise.all([
      api.get("/mentor-reports").then((res) => res.data).catch(() => []),
      api.get("/teams").then((res) => res.data).catch(() => []),
    ]);
    setReports(r || []);
    setTeams(t || []);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ team_id: teams[0]?.id || "", title: "", body: "", shared_with_team: false });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ team_id: r.team_id || "", title: r.title || "", body: r.body || "", shared_with_team: !!r.shared_with_team });
    setOpen(true);
  };

  const save = async () => {
    if (!form.team_id || !form.title.trim() || !form.body.trim()) {
      toast.error("Team, title and body are all required");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/mentor-reports/${editing.id}`, form);
        toast.success("Report updated");
      } else {
        await api.post("/mentor-reports", form);
        toast.success("Report created");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const remove = async (r) => {
    if (!(await confirm({ title: "Delete this report?", destructive: true }))) return;
    await api.delete(`/mentor-reports/${r.id}`);
    await load();
  };

  const teamName = (tid) => teams.find((t) => t.id === tid)?.name || "—";

  return (
    <div className="space-y-6" data-testid="mentor-reports-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-3xl font-black tracking-tight text-[#0F1F2E]">
            {isAdmin ? "All mentor reports" : "My mentor reports"}
          </h2>
          <p className="text-sm text-[#525252] mt-1">
            {isAdmin
              ? "Every report every mentor has written. Reports marked shared are visible to the team as well."
              : isMentor
              ? "Reports are private by default (only you and admin see them). Toggle 'Share with team' to release."
              : "Advice and reports your mentors have shared with your team."}
          </p>
        </div>
        {isMentor && (
          <Button onClick={openNew} className="bg-[#25AAAA] hover:bg-[#1e7d7d] rounded-none" data-testid="report-new-btn">
            <Plus size={16} className="mr-2" /> New report
          </Button>
        )}
        {isAdmin && (
          <Button
            onClick={async () => {
              if (!window.confirm("Email every mentor a digest of their teams' problem statements and canvas activity?")) return;
              const t = toast.loading("Sending digests…");
              try {
                const { data } = await api.post("/mentor-digest/send");
                toast.success(`Sent ${data.sent}${data.skipped ? ` · ${data.skipped} skipped (no data yet)` : ""}${data.failed ? ` · ${data.failed} failed` : ""}`, { id: t });
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Send failed", { id: t });
              }
            }}
            className="bg-[#0F1F2E] hover:bg-[#25AAAA] rounded-none"
            data-testid="admin-mentor-digest-btn"
          >
            <Send size={16} className="mr-2" /> Send mentor digest
          </Button>
        )}
      </div>

      {reports.length === 0 ? (
        <div className="border-2 border-dashed border-[#E5E7EB] bg-white p-10 text-center" data-testid="reports-empty">
          <MessageSquare size={32} className="mx-auto text-[#525252]" />
          <p className="text-sm text-[#525252] mt-3">
            {isMentor ? "No reports yet. Write your first one to keep a record of your advice." : "No reports yet."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <div key={r.id} className="border border-[#E5E7EB] bg-white p-5" data-testid={`report-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#25AAAA]">{teamName(r.team_id)}</div>
                    {r.shared_with_team ? (
                      <span className="text-[10px] uppercase tracking-wider bg-[#25AAAA] text-white px-2 py-0.5 font-semibold inline-flex items-center gap-1"><Eye size={9}/>Shared with team</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider bg-[#F3F4F6] text-[#525252] border border-[#E5E7EB] px-2 py-0.5 font-semibold inline-flex items-center gap-1"><EyeOff size={9}/>Private</span>
                    )}
                    {(isAdmin || !isMentor) && <span className="text-[10px] uppercase tracking-wider text-[#525252]">by {r.mentor_name}</span>}
                  </div>
                  <h3 className="font-display text-xl font-bold text-[#0F1F2E] mt-1">{r.title}</h3>
                  <p className="text-sm text-[#0F1F2E] whitespace-pre-wrap mt-3 leading-relaxed">{r.body}</p>
                  <div className="text-[11px] text-[#525252] mt-3">{new Date(r.created_at).toLocaleString()}</div>
                </div>
                {(isMentor && r.mentor_user_id === user.id) || isAdmin ? (
                  <div className="flex gap-2 flex-shrink-0">
                    {isMentor && r.mentor_user_id === user.id && (
                      <Button size="sm" variant="outline" className="rounded-none" onClick={() => openEdit(r)} data-testid={`report-edit-${r.id}`}>
                        <Pencil size={12} className="mr-1"/> Edit
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="rounded-none border-[#E93E36] text-[#E93E36] hover:bg-[#FDEDED]" onClick={() => remove(r)} data-testid={`report-delete-${r.id}`}>
                      <Trash2 size={12} className="mr-1"/> Delete
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit report" : "New report"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Team *</Label>
              <Select value={form.team_id} onValueChange={(v) => setForm({ ...form, team_id: v })}>
                <SelectTrigger data-testid="report-team-select"><SelectValue placeholder="Pick a team" /></SelectTrigger>
                <SelectContent>
                  {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="report-input-title"/>
            </div>
            <div>
              <Label>Body *</Label>
              <Textarea rows={8} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="report-input-body"/>
            </div>
            <div className="flex items-center justify-between border border-[#E5E7EB] p-3">
              <div>
                <div className="text-sm font-semibold text-[#0F1F2E]">Share with the team</div>
                <div className="text-xs text-[#525252] mt-0.5">Off: only you and admin can see this. On: visible to the team and admin.</div>
              </div>
              <Switch checked={form.shared_with_team} onCheckedChange={(v) => setForm({ ...form, shared_with_team: !!v })} data-testid="report-shared-toggle"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={busy} className="bg-[#25AAAA] hover:bg-[#1e7d7d] rounded-none" onClick={save} data-testid="report-save-btn">
              {busy ? "Saving…" : (editing ? "Save changes" : "Create report")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
